/**
 * queue/components/atoms.tsx
 * Small monochrome building blocks shared across surfaces. Kept here (not the
 * catalog barrel) because they're queue-specific: the SK mark, mono text, the
 * pulsing status dot, a state chip, a ± stepper, and the tier pill.
 */

import React, { useEffect, useRef } from 'react';
import { View, Text, Animated, TouchableOpacity, TextStyle, ViewStyle, StyleProp } from 'react-native';
import { useTheme } from '../../context/themeContext';
import { stateStyle } from '../stateStyle';
import type { EntryState } from '../types';

/** Mono-font text (numbers, tokens, timers, clocks). */
export function Mono({
  children,
  style,
  numberOfLines,
  adjustsFontSizeToFit,
}: {
  children: React.ReactNode;
  style?: StyleProp<TextStyle>;
  // Optional passthroughs so a Mono value can be constrained to its container
  // (e.g. the guest "your table" card shrinking a long value to fit one line).
  numberOfLines?: number;
  adjustsFontSizeToFit?: boolean;
}) {
  return (
    <Text
      style={[{ fontFamily: 'IBM Plex Mono' }, style]}
      numberOfLines={numberOfLines}
      adjustsFontSizeToFit={adjustsFontSizeToFit}
    >
      {children}
    </Text>
  );
}

/** The "SK" bordered mark. */
export function SkMark({ size = 26, color }: { size?: number; color?: string }) {
  const { theme } = useTheme();
  const c = color ?? theme.colors.ink;
  return (
    <View style={{
      width: size, height: size, borderWidth: 1.5, borderColor: c, borderRadius: size * 0.23,
      alignItems: 'center', justifyContent: 'center',
    }}>
      <Mono style={{ fontWeight: '600', fontSize: size * 0.5, color: c }}>SK</Mono>
    </View>
  );
}

/** A dot that fades-pulses (you're-next / called). */
export function PulseDot({ style }: { style?: StyleProp<ViewStyle> }) {
  const opacity = useRef(new Animated.Value(1)).current;
  useEffect(() => {
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(opacity, { toValue: 0.35, duration: 650, useNativeDriver: true }),
        Animated.timing(opacity, { toValue: 1, duration: 650, useNativeDriver: true }),
      ]),
    );
    loop.start();
    return () => loop.stop();
  }, [opacity]);
  return <Animated.View style={[{ opacity }, style]} />;
}

/** State chip (Waiting / You're next / Seated / …) — monochrome, ported. */
export function StateChip({ state, compact }: { state: EntryState; compact?: boolean }) {
  const s = stateStyle(state);
  const chip = compact ? s.chip : { ...s.chip, height: 27 };
  return (
    <View style={chip}>
      {s.pulse
        ? <PulseDot style={s.dot} />
        : <View style={s.dot} />}
      <Text style={s.chipText}>{s.label}</Text>
    </View>
  );
}

/** Small status pill shown in a surface header (e.g. "READ-ONLY"). */
export function TierPill({ label, solid }: { label: string; solid?: boolean }) {
  const { theme } = useTheme();
  const { colors } = theme;
  return (
    <View style={{
      flexDirection: 'row', alignItems: 'center', height: 24, paddingHorizontal: 11, borderRadius: 999,
      backgroundColor: solid ? colors.ink : colors.card,
      borderWidth: solid ? 0 : 1, borderColor: colors.lineStrong,
    }}>
      <Mono style={{ fontSize: 11, fontWeight: '600', color: solid ? colors.white : colors.ink2 }}>{label}</Mono>
    </View>
  );
}

/** ± stepper used by guest party + walk-in + max-party. */
export function Stepper({
  value, onMinus, onPlus, size = 'md', hint,
}: {
  value: number | string; onMinus: () => void; onPlus: () => void;
  size?: 'md' | 'lg'; hint?: string;
}) {
  const { theme } = useTheme();
  const { colors } = theme;
  const big = size === 'lg';
  const btn = big ? 56 : 40;
  const radius = big ? 16 : 10;
  const numSize = big ? 46 : 26;

  const Btn = ({ label, dark, onPress }: { label: string; dark?: boolean; onPress: () => void }) => (
    <TouchableOpacity
      onPress={onPress}
      style={{
        width: btn, height: btn, borderRadius: radius,
        borderWidth: 1.5, borderColor: colors.ink,
        backgroundColor: dark ? colors.ink : colors.card,
        alignItems: 'center', justifyContent: 'center',
      }}
    >
      <Text style={{ fontSize: big ? 26 : 20, fontWeight: '600', color: dark ? colors.white : colors.ink }}>{label}</Text>
    </TouchableOpacity>
  );

  return (
    <View>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: big ? 14 : 10 }}>
        <Btn label="–" onPress={onMinus} />
        <View style={{ flex: big ? 1 : undefined, minWidth: big ? undefined : 40, alignItems: 'center' }}>
          <Mono style={{ fontSize: numSize, fontWeight: '600', color: colors.ink, lineHeight: numSize }}>{value}</Mono>
        </View>
        <Btn label="+" dark onPress={onPlus} />
      </View>
      {hint ? <Text style={{ fontSize: 11, color: colors.ink4, marginTop: 8, textAlign: 'center' }}>{hint}</Text> : null}
    </View>
  );
}
